"use client";

import { useState, useRef } from "react";
import { useBlogTheme } from "@/contexts/blog-theme-context";

interface Bar {
  label: string;
  val: number;
  type: "a" | "c"; // a = Agentica, c = CoT
}

interface Group {
  name: string;
  bars: Bar[];
}

const groups: Group[] = [
  {
    name: "Opus 4.6 (120k) High",
    bars: [
      { label: "Agentica", val: 85.28, type: "a" },
      { label: "CoT", val: 79.03, type: "c" },
    ],
  },
  {
    name: "GPT 5.2 (XHigh)",
    bars: [
      { label: "Agentica", val: 70.27, type: "a" },
      { label: "CoT", val: 59.81, type: "c" },
    ],
  },
  {
    name: "Opus 4.5",
    bars: [
      { label: "Agentica", val: 49.58, type: "a" },
      { label: "(32k)", val: 28.15, type: "c" },
    ],
  },
];

const legendItems = [
  { name: "Agentica", type: "a" as const },
  { name: "CoT Baseline", type: "c" as const },
];

export function BarChart() {
  const { resolvedTheme } = useBlogTheme();
  const isDark = resolvedTheme === "dark";
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    bar: Bar | null;
    group: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    bar: null,
    group: "",
  });

  const colA = isDark ? "#8b7bd4" : "#7668c7";
  const colC = isDark ? "#666" : "#aaa";

  // Chart dimensions
  const W = 960,
    H = 540;
  const pad = { t: 65, r: 60, b: 100, l: 70 };
  const fs = { ax: 17, at: 17, val: 16, leg: 17, title: 22, sub: 15, grp: 18 };
  const pW = W - pad.l - pad.r;
  const pH = H - pad.t - pad.b;
  const maxV = 100;

  const yScale = (v: number) => pad.t + pH - (v / maxV) * pH;

  // Bar layout calculations
  const totalBars = groups.reduce((a, g) => a + g.bars.length, 0);
  const gapW = 112;
  const gaps = groups.length - 1;
  const barGap = 20;
  const totalInternalGaps = groups.reduce((a, g) => a + (g.bars.length - 1), 0);
  const availW = pW - gaps * gapW - totalInternalGaps * barGap;
  const barW = Math.min(56, availW / totalBars);

  const totalChartW =
    groups.reduce(
      (a, g) => a + g.bars.length * barW + (g.bars.length - 1) * barGap,
      0
    ) +
    gaps * gapW;
  const startX = pad.l + (pW - totalChartW) / 2;

  // Y-axis ticks
  const yTicks = [0, 25, 50, 75, 100];

  // Legend positioning
  const legItemW = 180;
  const legTotalW = legendItems.length * legItemW;
  const legStartX = W / 2 - legTotalW / 2;

  const themeColors = {
    bg: isDark ? "#1a1a1a" : "#FFFDF0",
    text: isDark ? "#fff" : "#1a1a1a",
    axisLabel: isDark ? "#888" : "#555",
    axisTitle: isDark ? "#ccc" : "#1a1a1a",
    gridLine: isDark ? "#222" : "#e0dfd9",
    axisLine: isDark ? "#444" : "#ccc",
    legendText: isDark ? "#999" : "#555",
    tooltipBg: isDark ? "#1e1e1e" : "#fff",
    tooltipBorder: isDark ? "#2a2a2a" : "#e0dfd9",
    tooltipText: isDark ? "#fff" : "#1a1a1a",
    tooltipLabel: "#888",
    tooltipVal: isDark ? "#e0e0e0" : "#1a1a1a",
    separatorLine: isDark ? "#333" : "#ddd",
  };

  const handleMouseEnter = (bar: Bar, group: string, e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left + 16;
    let y = e.clientY - rect.top - 10;
    if (x + 220 > rect.width) x = e.clientX - rect.left - 230;
    if (y + 100 > rect.height) y = y - 100;
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    setTooltip({ visible: true, x, y, bar, group });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!tooltip.visible || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left + 16;
    let y = e.clientY - rect.top - 10;
    if (x + 220 > rect.width) x = e.clientX - rect.left - 230;
    if (y + 100 > rect.height) y = y - 100;
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    setTooltip((prev) => ({ ...prev, x, y }));
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, bar: null, group: "" });
  };

  // Calculate bar positions
  let currentX = startX;
  const barPositions: {
    group: Group;
    bar: Bar;
    x: number;
    gi: number;
    bi: number;
  }[] = [];
  const groupPositions: {
    group: Group;
    startX: number;
    width: number;
    gi: number;
  }[] = [];

  groups.forEach((g, gi) => {
    const groupStartX = currentX;
    const groupWidth = g.bars.length * barW + (g.bars.length - 1) * barGap;
    groupPositions.push({
      group: g,
      startX: groupStartX,
      width: groupWidth,
      gi,
    });

    g.bars.forEach((b, bi) => {
      barPositions.push({ group: g, bar: b, x: currentX, gi, bi });
      currentX += barW + barGap;
    });
    currentX -= barGap;
    currentX += gapW;
  });

  return (
    <div style={{ width: "100%" }} className="bar-chart__box">
      <div
        ref={containerRef}
        className="bar-chart__container"
        style={{
          position: "relative",
          overflow: "visible",
          padding: "30px 40px",
        }}
      >
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="bar-chart__svg"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            overflow: "visible",
          }}
        >
          {/* Title */}
          <text
            x={W / 2}
            y={pad.t - 38}
            textAnchor="middle"
            fill={themeColors.text}
            style={{
              fontSize: `${fs.title}px`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 600,
            }}
          >
            ARC-AGI-2 Public Eval: Agentica vs. CoT
          </text>

          {/* Y-axis grid lines and labels */}
          {yTicks.map((v) => (
            <g key={`y-${v}`}>
              <line
                x1={pad.l}
                y1={yScale(v)}
                x2={W - pad.r}
                y2={yScale(v)}
                stroke={themeColors.gridLine}
                strokeWidth={1}
              />
              <text
                x={pad.l - 10}
                y={yScale(v) + 4}
                textAnchor="end"
                fill={themeColors.axisLabel}
                style={{
                  fontSize: `${fs.ax}px`,
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                {v}
              </text>
            </g>
          ))}

          {/* Y-axis title */}
          <text
            x={0}
            y={pad.t + pH / 2}
            textAnchor="middle"
            transform={`rotate(-90, 0, ${pad.t + pH / 2})`}
            fill={themeColors.axisTitle}
            style={{
              fontSize: `${fs.at}px`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontWeight: 400,
            }}
          >
            Score (%)
          </text>

          {/* Baseline */}
          <line
            x1={pad.l}
            y1={yScale(0)}
            x2={W - pad.r}
            y2={yScale(0)}
            stroke={themeColors.axisLine}
            strokeWidth={1}
          />

          {/* Bars */}
          {barPositions.map(({ group, bar, x, gi, bi }) => {
            const col = bar.type === "a" ? colA : colC;
            const by = yScale(bar.val);
            const bh = yScale(0) - by;
            const lines = bar.label.split("\n");
            const isBold = bar.type === "a";

            return (
              <g key={`${gi}-${bi}`}>
                {bh > 0 && (
                  <>
                    <rect
                      x={x}
                      y={by}
                      width={barW}
                      height={bh}
                      rx={2}
                      fill={col}
                      fillOpacity={isDark ? 0.9 : 0.85}
                      style={{
                        cursor: "pointer",
                        transition: "filter 0.2s ease",
                      }}
                      onMouseEnter={(e) => handleMouseEnter(bar, group.name, e)}
                      onMouseMove={handleMouseMove}
                      onMouseLeave={handleMouseLeave}
                    />
                    <text
                      x={x + barW / 2}
                      y={by - 7}
                      textAnchor="middle"
                      fill={themeColors.text}
                      style={{
                        fontSize: `${fs.val}px`,
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontWeight: 600,
                        pointerEvents: "none",
                      }}
                    >
                      {bar.val}
                    </text>
                  </>
                )}
                {/* Bar labels */}
                {lines.map((ln, li) => {
                  const isSecondaryLine = ln.includes("8 agents");
                  return (
                    <text
                      key={li}
                      x={x + barW / 2}
                      y={yScale(0) + 16 + li * 13}
                      textAnchor="middle"
                      fill={
                        isBold && !isSecondaryLine
                          ? themeColors.text
                          : themeColors.axisLabel
                      }
                      style={{
                        fontSize: `${fs.sub}px`,
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontWeight: isBold && !isSecondaryLine ? 600 : 300,
                      }}
                    >
                      {ln}
                    </text>
                  );
                })}
              </g>
            );
          })}

          {/* Group labels and separators */}
          {groupPositions.map(({ group, startX, width, gi }) => (
            <g key={gi}>
              <text
                x={startX + width / 2}
                y={yScale(0) + 58}
                textAnchor="middle"
                fill={themeColors.text}
                style={{
                  fontSize: `${fs.grp}px`,
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontWeight: 600,
                }}
              >
                {group.name}
              </text>
              {gi < groups.length - 1 && (
                <line
                  x1={startX + width + gapW / 2}
                  y1={yScale(0) + 4}
                  x2={startX + width + gapW / 2}
                  y2={yScale(0) + 64}
                  stroke={themeColors.separatorLine}
                  strokeWidth={1}
                  strokeDasharray="3,3"
                />
              )}
            </g>
          ))}

          {/* Legend */}
          {legendItems.map((it, i) => {
            const col = it.type === "a" ? colA : colC;
            const x = legStartX + i * legItemW;
            return (
              <g key={it.type}>
                <circle
                  cx={x + 6}
                  cy={yScale(0) + 92}
                  r={5}
                  fill={col}
                  opacity={isDark ? 0.9 : 0.85}
                />
                <text
                  x={x + 16}
                  y={yScale(0) + 100}
                  fill={themeColors.legendText}
                  style={{
                    fontSize: `${fs.leg}px`,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontWeight: isDark ? 300 : 400,
                  }}
                >
                  {it.name}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {tooltip.visible && tooltip.bar && (
          <div
            style={{
              position: "absolute",
              left: tooltip.x,
              top: tooltip.y,
              pointerEvents: "none",
              background: themeColors.tooltipBg,
              border: `1px solid ${themeColors.tooltipBorder}`,
              borderRadius: "10px",
              padding: "14px 18px",
              zIndex: 100,
              minWidth: "200px",
              boxShadow: isDark
                ? "0 4px 20px rgba(0,0,0,.4)"
                : "0 4px 20px rgba(0,0,0,.08)",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "3px",
                borderRadius: "2px",
                marginBottom: "10px",
                background: tooltip.bar.type === "a" ? colA : colC,
              }}
            />
            <div
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: themeColors.tooltipText,
                marginBottom: "8px",
                lineHeight: 1.3,
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              {tooltip.bar.label.replace(/\n/g, " ")}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "0.72rem",
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              <span
                style={{ color: themeColors.tooltipLabel, fontWeight: 300 }}
              >
                Group
              </span>
              <span style={{ color: themeColors.tooltipVal, fontWeight: 400 }}>
                {tooltip.group}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "4px",
                fontSize: "0.72rem",
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              <span
                style={{ color: themeColors.tooltipLabel, fontWeight: 300 }}
              >
                Score
              </span>
              <span style={{ color: themeColors.tooltipVal, fontWeight: 400 }}>
                {tooltip.bar.val}%
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
